import { PortalTelemetryEvent } from '../../app/telemetry/portalTelemetryEvents';
import { PcsEnrolmentStepEnum } from '../../app/models/pcsEnrolment/pcsEnrolmentStepEnum';
import { shouldPreparePcsOnlyPayment, getPaymentRedirectProps, validatePaymentRedirect, isPcsDiscountApplicable, preparePcsPayment } from './pcsPaymentLogic';
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../app/store/configureStore";
import { useMsal } from "@azure/msal-react";
import { IdTokenClaims } from "../../app/models/account/idTokenClaims";
import { useEffect, useRef, useState } from "react";
import { ApiRequestStatus } from "../../app/enums/apiRequestStatus";
import LoadingComponent from "../../app/layout/LoadingComponent";
import { canPayMembershipAsync, enrolBasketAsync, finalisePaymentCheckAsync } from "../../app/slices/pcsEnrolmentSlice";
import { getPaymentIdFromBasketAsync } from "../../app/slices/shoppingBasketSlice";
import { getPaymentRedirectAsync, getPaymentSummaryAsync } from "../../app/slices/paymentSlice";
import { MESSAGE_PROCESSING_DETAILS } from "../../app/utils/constant";
import { AppPathEnum } from "../../app/enums/appPathEnum";
import { useHistory } from "react-router-dom";
import { beginPcsPayment, flushPcsTelemetry, trackPcsEvent, trackPcsOnce } from '../../app/telemetry/portalTelemetry';
import { toast } from 'react-toastify';
import { CurrencyCodeIconEnum } from "../../app/enums/currencyCodeIconEnum";
import PcsSubscriptionFeesDue from "./PcsSubscriptionFeesDue";

interface Prop {
  reenrol: boolean;
}

export default function Payment({ reenrol }: Prop) {
  const { instance } = useMsal();
  const userClaims = instance.getActiveAccount()?.idTokenClaims as unknown as IdTokenClaims;

  const { canPayMembership, canPayMembershipStatus, finalisePaymentCheck, finalisePaymentCheckStatus }
    = useAppSelector(state => state.pcsEnrolment);

  const dispatch = useAppDispatch();
  const history = useHistory();
  const paymentChecksStarted = useRef(false);
  const paymentPreparationStarted = useRef(false);
  const redirectPending = useRef(false);
  const [isPaymentChecksLoading, setIsPaymentChecksLoading] = useState(true);
  const [isPaymentSummaryLoading, setIsPaymentSummaryLoading] = useState(false);
  const [paymentId, setPaymentId] = useState<string>();

  useEffect(() => {
    if (paymentChecksStarted.current) return;
    paymentChecksStarted.current = true;

    const checks: Promise<unknown>[] = [];
    if (canPayMembershipStatus === ApiRequestStatus.Idle) {
      checks.push(dispatch(canPayMembershipAsync()));
    }
    if (reenrol === true && finalisePaymentCheckStatus === ApiRequestStatus.Idle) {
      checks.push(dispatch(finalisePaymentCheckAsync()));
    }

    if (checks.length === 0) {
      setIsPaymentChecksLoading(false);
      return;
    }

    void Promise.all(checks).finally(() => setIsPaymentChecksLoading(false));
  }, [dispatch, reenrol, canPayMembershipStatus, finalisePaymentCheckStatus]);

  const [canRedirectToPaymentSummary, setCanRedirectToPaymentSummary] = useState(false);
  const [isCombined, setIsCombined] = useState(false);
  const { paymentDetails } = useAppSelector(state => state.payment);
  const { canEnrol, canReEnrol } = useAppSelector(state => state.pcs);
  const { countries, famMembershipTypes } = useAppSelector(state => state.ReferenceData);
  const { famDetails } = useAppSelector(state => state.famDetails);

  useEffect(() => {
    if (shouldPreparePcsOnlyPayment(reenrol, canPayMembership, finalisePaymentCheck)) {
      trackPcsOnce(PortalTelemetryEvent.PaymentOptionSelected, 'option:pcs_only', { paymentOption: 'pcs_only' });
      setIsPaymentSummaryLoading(true);
      setCanRedirectToPaymentSummary(true);
    }
  }, [canPayMembership, finalisePaymentCheck, reenrol]);

  useEffect(() => {
    if (!canRedirectToPaymentSummary || paymentPreparationStarted.current) return;
    paymentPreparationStarted.current = true;
    setIsPaymentSummaryLoading(true);

    const preparePayment = async () => {
      try {
        const nextPaymentId = await preparePcsPayment(isCombined, {
          enrolBasket: combined => dispatch(enrolBasketAsync(combined)).unwrap(),
          getPaymentId: basketId => dispatch(getPaymentIdFromBasketAsync(basketId)).unwrap(),
          loadSummary: id => dispatch(getPaymentSummaryAsync(id)).unwrap()
        });
        setPaymentId(nextPaymentId);
      } catch {
        toast.error('Unable to prepare the payment summary.');
      } finally {
        setIsPaymentSummaryLoading(false);
      }
    };

    void preparePayment();
  }, [dispatch, canRedirectToPaymentSummary, isCombined]);

  const handlePay = async () => {
    if (!paymentId || redirectPending.current) return;
    redirectPending.current = true;
    setIsPaymentSummaryLoading(true);

    const isEligible = reenrol ? canReEnrol : canEnrol;
    if (isEligible !== true) {
      redirectPending.current = false;
      setIsPaymentSummaryLoading(false);
      if (isEligible === false) trackPcsOnce(PortalTelemetryEvent.EligibilityBlocked, 'blocked');
      return;
    }

    beginPcsPayment(isCombined ? 'combined' : 'pcs_only');
    const paymentRedirectProps = getPaymentRedirectProps(paymentId, window.location, String(process.env.REACT_APP_REDIRECT_URI));

    try {
      const redirect = await dispatch(getPaymentRedirectAsync(paymentRedirectProps)).unwrap();
      validatePaymentRedirect(redirect);
      trackPcsEvent(PortalTelemetryEvent.PaymentRedirect, { outcome: 'succeeded', paymentOption: isCombined ? 'combined' : 'pcs_only' });
      flushPcsTelemetry();
      window.location.replace(redirect);
    } catch {
      trackPcsEvent(PortalTelemetryEvent.PaymentRedirect, { outcome: 'invalid_redirect' });
      toast.error('Unable to open the payment provider. Please try again.');
      redirectPending.current = false;
      setIsPaymentSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (reenrol && finalisePaymentCheckStatus === ApiRequestStatus.Fulfilled && finalisePaymentCheck === false) {
      trackPcsOnce(PortalTelemetryEvent.StepViewed, 'step:4', { step: PcsEnrolmentStepEnum.Payment });
      trackPcsOnce(PortalTelemetryEvent.PaymentOptionSelected, 'option:prepaid', { paymentOption: 'prepaid' });
      history.push(`${AppPathEnum.PREPAID_PAYMENT_SUCCESS_URI}${window.location.search}`);
    }
  }, [reenrol, finalisePaymentCheckStatus, finalisePaymentCheck, history]);

  const isLoading = isPaymentChecksLoading
    || canPayMembershipStatus === ApiRequestStatus.Pending
    || finalisePaymentCheckStatus === ApiRequestStatus.Pending
    || isPaymentSummaryLoading;

  return (
    <>
      {isLoading && <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />}
      <Box sx={{ flexGrow: 1, display: isLoading ? 'none' : 'block' }} component={Paper}>
        <Grid container rowSpacing={4} sx={{ margin: "2%" }}>

          <Grid size={{ xs: 12 }}></Grid>

          {canPayMembership === true && canRedirectToPaymentSummary === false &&
            <>
              <Grid size={{ xs: 12 }}>
                <Typography variant='h4'>Choose payment</Typography>
              </Grid>

              <Grid size={{ xs: 12 }} sx={{ marginRight: "5%" }}>
                <i>
                  If you are a Member or Fellow and practice in the Republic of Ireland,
                  you can avail of a discounted Professional Competence Scheme enrolment
                  fee <span style={{ color: 'red' }}>IF</span> you pay both PCS fee and Membership or Fellowship subscription
                  together on or before 1 July of the current PCS year.
                </i>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button variant="contained" color="primary" onClick={() => {
                  trackPcsEvent(PortalTelemetryEvent.PaymentOptionSelected, { paymentOption: 'combined' });
                  setIsPaymentSummaryLoading(true);
                  paymentPreparationStarted.current = false;
                  setPaymentId(undefined);
                  setCanRedirectToPaymentSummary(true);
                  setIsCombined(true);
                }}>
                  Pay both PCS & Membership together
                </Button>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button variant="contained" color="primary" onClick={() => {
                  trackPcsEvent(PortalTelemetryEvent.PaymentOptionSelected, { paymentOption: 'pcs_only' });
                  setIsPaymentSummaryLoading(true);
                  paymentPreparationStarted.current = false;
                  setPaymentId(undefined);
                  setCanRedirectToPaymentSummary(true);
                  setIsCombined(false);
                }}>
                  Pay PCS only
                </Button>
              </Grid>
            </>
          }
          {canRedirectToPaymentSummary === true &&
            <>
              <Grid size={{ xs: 12 }}></Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant='h4'>Payment summary</Typography>
              </Grid>

              <Grid size={{ xs: 12 }}></Grid>

              <Grid size={{ xs: 11.5, sm: 11.5 }} sx={{ backgroundColor: "#b9b9b9", padding: "1%" }}>
                <PcsSubscriptionFeesDue paymentDetails={paymentDetails} />
              </Grid>

              <Grid size={{ xs: 11.5 }} sx={{ fontStyle: 'italic' }}>
                {isPcsDiscountApplicable(famMembershipTypes?.find(x => x.id === famDetails?.membershipTypeId)?.code,
                  countries?.find(x => x.id === famDetails?.countryOfPracticeId)?.alpha3Code) &&
                  <>
                    <Typography variant='subtitle1'>Professional Competence Scheme (PCS) Surgery</Typography>
                    <br></br>
                    <Typography variant='subtitle1'>If you are a Member or Fellow and practice in the Republic of Ireland, you are entitled
                      to a {CurrencyCodeIconEnum.EUR}150 discount on your Professional Competence Scheme (PCS) fee if you pay
                      before 1 July of the current PCS year. *The PCS & Membership Year runs from
                      1st May - 30th April each year.
                    </Typography>
                  </>
                }
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant='subtitle1'>
                  Please note that by clicking on the PAY BY CARD link below you
                  will be brought to a third party system for payment processing.
                </Typography>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button variant="contained" color="primary" disabled={!paymentId || redirectPending.current} onClick={handlePay}>
                  Pay by card
                </Button>
              </Grid>

              {userClaims?.extension_portalcrm_famIGS === false &&
                userClaims?.extension_portalcrm_famFeesDue === true &&
                <Grid size={{ xs: 12 }}>
                  <Button variant="contained" color="primary" onClick={() => {
                    trackPcsEvent(PortalTelemetryEvent.Navigation, { step: PcsEnrolmentStepEnum.Payment, direction: 'payment_options' });
                    setIsPaymentSummaryLoading(false);
                    paymentPreparationStarted.current = false;
                    setPaymentId(undefined);
                    setCanRedirectToPaymentSummary(false);
                  }}>
                    Back to payment options
                  </Button>
                </Grid>}
            </>
          }

          <Grid size={{ xs: 12 }}></Grid>

        </Grid>
      </Box>
    </>
  );
}
